import { createReducer, on } from "@ngrx/store";

import { initialState } from "./user.state";
import { User } from "../interfaces/user.interface";
import { USER_STATUS } from "../user.status";
import { UserState } from "../interfaces/user-state.interface";

import {
  loadToggleSidenav,
  loadLogonsList,
  loadLogonsListSuccess,
  loadSelectedLogon,
  loadSelectedLogonSuccess,
  loadAccountResponseFailure,
  loadResetUser,
  loadSearchSuccess,
  doTablePagination,
  filterByLogon,
  loadUpdateAccountResponseFailure,
  loadResetSelectedUser,
  loadResetUpdateAccountFailure,
  updateSelectedLogon,
  loadSelectedLogonFailure,
  loadUpdateSelectedLogonSuccess,
  setEmptyData,
  resetUserSuccess,
  resetUserFailure,
  loadCloneableLogonSearchDataSuccess,
  loadCloneableLogonSearchDataFailure,
  cleanClonedUser,
  addClonedUserSuccess,
  addClonedUserFailure,
  loadUserMeInfoSuccess,
  loadMyAudits,
  loadMyAuditsSuccess,
  loadMyAuditsFailure,
  loadResetMyAudits,
  searchCloneLogonByNameEmailLogon,
  searchCloneLogonByNameEmailLogonSuccess,
  searchCloneLogonByNameEmailLogonFailure,
  clearCloneLogonSearch,
} from "./actions/user.actions";
import { Logon } from "../interfaces/logon.interface";

export const userReducer = createReducer(
  initialState,
  on(loadLogonsList, (state): UserState => ({ ...state, isLoading: true })),
  on(loadLogonsListSuccess, (state, { accountResponse }): any => ({
    ...state,
    logons: accountResponse.logons,
    data: usersWithStatus(accountResponse.logons),
    accountResponse: {
      page: accountResponse.page,
      company: accountResponse.company,
    },
    selectedLogon: _findByUserLogon(
      usersWithStatus(accountResponse.logons),
      state.selectedLogon,
    ),
    showEmptyList: listIsEmpty(accountResponse.logons),
    error: false,
    isLoading: false,
  })),
  on(loadSearchSuccess, (state, { accountResponse }): any => ({
    ...state,
    logons: accountResponse.logons,
    data: usersWithStatus(accountResponse.logons),
    accountResponse: {
      page: accountResponse.page,
      company: accountResponse.company,
    },
    showEmptyList: listIsEmpty(accountResponse.logons),
    error: false,
    isLoading: false,
  })),
  on(doTablePagination, (state, { accountResponse }): any => ({
    ...state,
    logons: accountResponse.logons,
    data: usersWithStatus(accountResponse.logons),
    isLoading: false,
    error: false,
  })),
  on(
    loadAccountResponseFailure,
    (state, { error }): UserState => ({
      ...state,
      data: [],
      error,
      isLoading: false,
    }),
  ),
  on(
    loadUpdateAccountResponseFailure,
    (state, { error }): UserState => ({
      ...state,
      updateUserAccount: {
        error,
        isLoading: false,
      },
      selectedLogon: {
        ...state.selectedLogon,
        alternativeEmail: {},
        error: error,
      },
    }),
  ),
  on(
    loadResetUpdateAccountFailure,
    (state): UserState => ({
      ...state,
      updateUserAccount: { error: null, isLoading: false },
    }),
  ),
  on(setEmptyData, (state: UserState): UserState => {
    return {
      ...state,
      data: [],
      showEmptyList: true,
    };
  }),
  on(
    loadSelectedLogon,
    (state, { selectedLogon }): UserState => ({ ...state, selectedUserId: selectedLogon }),
  ),
  on(
    loadUserMeInfoSuccess,
    (state, { userMeInfo }): UserState => ({ ...state, userMeInfo }),
  ),
  on(
    updateSelectedLogon,
    (state, { logon }): UserState => ({
      ...state,
      selectedUserId: logon,
      updateUserAccount: { error: null, isLoading: true },
    }),
  ),
  on(loadUpdateSelectedLogonSuccess, (state, { payload }): UserState => {
    let styleClass = "";
    if (state.selectedLogon?.status === "BLOCKED") {
      styleClass = "--block";
    } else {
      styleClass = "--active";
    }
    return {
      ...state,
      updateUserAccount: { error: null, isLoading: false },
      selectedLogon: {
        ...state.selectedLogon,
        error: null,
        fullname: payload.fullName,
        alternativeEmail: { email: payload.email },
        phone: payload.phone,
        document: payload.document,
        lastUpdateAt: new Date().toISOString(),
        statusClass: styleClass,
      },
      error: null,
    };
  }),
  on(
    loadSelectedLogonSuccess,
    (state, { selectedLogon }): UserState => ({
      ...state,
      selectedLogon,
    }),
  ),
  on(
    loadCloneableLogonSearchDataSuccess,
    (state, { cloneableLogon }): UserState => ({
      ...state,
      cloneableLogonSearch: {
        logon: cloneableLogon,
        error: null,
      },
    }),
  ),
  on(
    loadCloneableLogonSearchDataFailure,
    (state, { error }): UserState => ({
      ...state,
      cloneableLogonSearch: { error },
    }),
  ),
  on(addClonedUserSuccess, (state, { response }): UserState => {
    const succeeded = response.filter((r) => !!r.response);
    const failed = response.filter((r) => !!r.error);
    return {
      ...state,
      clonedLogon: {
        logon: succeeded.map((r) => r.response!),
        successResults: succeeded,
        failedEmails: failed.map((r) => r.email),
        failedResults: failed,
        error: failed.length > 0 ? failed[0].error! : null,
      },
    };
  }),
  on(
    addClonedUserFailure,
    (state, { error }): UserState => ({ ...state, clonedLogon: { error } }),
  ),
  on(
    cleanClonedUser,
    (state): UserState => ({
      ...state,
      cloneableLogonSearch: {
        logon: undefined,
        error: null,
      },
      clonedLogon: {
        logon: undefined,
        error: null,
      },
    }),
  ),
  on(
    resetUserSuccess,
    (state, { resetStatus }): UserState => ({
      ...state,
      resetStatus,
    }),
  ),
  on(
    resetUserFailure,
    (state, { error, resetStatus }): UserState => ({
      ...state,
      resetStatus,
    }),
  ),
  on(
    loadSelectedLogonFailure,
    (state, { error }): UserState => ({ ...state, selectedLogon: { error } }),
  ),
  on(
    loadToggleSidenav,
    (state, { openSidenav }): UserState => ({ ...state, openSidenav }),
  ),
  on(loadResetUser, () => Object.assign({}, initialState)),
  on(
    loadResetSelectedUser,
    (state): UserState => ({
      ...state,
      selectedUserId: "",
      userMeInfo: null,
      error: null,
      selectedLogon: {},
    }),
  ),
  on(
    filterByLogon,
    (state, { searchValue, searchType }): UserState => ({
      ...state,
      isLoading: true,
      filterParams: { searchValue, searchType },
    }),
  ),
  on(loadMyAudits, (state) => ({
    ...state,
    myAudits: {
      data: null,
      error: false,
    },
  })),
  on(loadResetMyAudits, (state) => ({
    ...state,
    myAudits: {
      data: null,
      error: false,
    },
  })),
  on(loadMyAuditsSuccess, (state, { myAudits }) => ({
    ...state,
    myAudits: {
      data: myAudits,
      error: false,
    },
  })),
  on(loadMyAuditsFailure, (state, { error }) => ({
    ...state,
    myAudits: {
      data: { logAudits: [] },
      error: true,
    },
  })),
  on(
    searchCloneLogonByNameEmailLogon,
    (state): UserState => ({
      ...state,
      cloneLogonSearch: {
        results: [],
        loading: true,
        error: null,
      },
    }),
  ),
  on(
    searchCloneLogonByNameEmailLogonSuccess,
    (state, { logons }): UserState => ({
      ...state,
      cloneLogonSearch: {
        results: logons,
        loading: false,
        error: null,
      },
    }),
  ),
  on(
    searchCloneLogonByNameEmailLogonFailure,
    (state, { error }): UserState => ({
      ...state,
      cloneLogonSearch: {
        results: [],
        loading: false,
        error,
      },
    }),
  ),
  on(
    clearCloneLogonSearch,
    (state): UserState => ({
      ...state,
      cloneLogonSearch: {
        results: [],
        loading: false,
        error: null,
      },
    }),
  ),
);

export const usersWithStatus = (users: User[]) => {
  const usersWithStatus = users.map((user) => {
    return {
      ...user,
      statusClass: USER_STATUS[user.status ?? ""] ?? "",
    };
  });
  return usersWithStatus;
};

export const listIsEmpty = (list: User[]): boolean => list?.length === 0;

export const _findByUserLogon = (
  logon: User[],
  oldSelectedLogon: Logon | undefined,
) => {
  if (!oldSelectedLogon) {
    return null;
  }

  const updatedLogon = logon.find(
    (item) => item.logon === oldSelectedLogon.logon,
  );

  if (!updatedLogon) {
    return null;
  }

  const mergedLogon = {
    ...oldSelectedLogon,
    ...updatedLogon,
  };

  return mergedLogon;
};
